import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { ArrowLineLeft, ArrowLineRight, ArrowsLeftRight, X, SignOut, Moon, Sun, Monitor, Bug, Question } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useTheme } from '../hooks/use-theme';
import type { ThemePref } from '../lib/theme';
import { Link, useLocation, useNavigate } from '../lib/routing';
import { useAuth } from '../hooks/use-auth';
import { useIsAdmin, useIsTeacher } from '../hooks/use-is-admin';
import { useDirectionCapabilities } from '../hooks/use-direction-capabilities';
import { useRoles, type Role, type RoleAssignment } from '../hooks/use-roles';
import { Bar, Divider, NaiaLogo } from './header/icons';
import { SideItem } from './header/side-item';
import { activeRailPath } from './header/rail-active';
import { railPaths } from './header/rail-nav';
import { RailNav } from './header/rail-nav';
import { RailRoleSwitcher } from './header/rail-role-switcher';
import { RailConvList } from './header/rail-conv-list';
import { RailClassList } from './header/rail-class-list';
import { ExamImportProgressRailItem } from './exam-import-progress';
import { BulkImportProgressRailItem } from './bulk-import-progress';
import { authClient } from '../lib/auth-client';
import { useSidemenuOpen } from './shell/sidemenu-store';
import './header-rail.css';

/* EDU-105: renamed from pilo.* — one-time migration copies existing preference */
const SIDEMENU_KEY = 'naia.sidemenu'; /* 'open' | 'closed' */
const MODE_KEY = 'naia.sidemenu.mode'; /* 'auto' | 'collapsed' | 'extended' */
try {
  if (!localStorage.getItem(SIDEMENU_KEY) && localStorage.getItem('pilo.sidemenu'))
    localStorage.setItem(SIDEMENU_KEY, localStorage.getItem('pilo.sidemenu')!);
  if (!localStorage.getItem(MODE_KEY) && localStorage.getItem('pilo.sidemenu.mode'))
    localStorage.setItem(MODE_KEY, localStorage.getItem('pilo.sidemenu.mode')!);
} catch { /* ignore */ }

type RailMode = 'auto' | 'collapsed' | 'extended';
const MODE_ORDER: RailMode[] = ['auto', 'collapsed', 'extended'];
const MODE_ICON: Record<RailMode, React.ReactNode> = {
  auto: <ArrowsLeftRight size={14} weight="bold" />,
  collapsed: <ArrowLineRight size={14} weight="bold" />,
  extended: <ArrowLineLeft size={14} weight="bold" />,
};
const MODE_HINT: Record<RailMode, string> = {
  auto: 'Menu : auto — replié, s’étend au survol. Cliquer pour passer en « replié ».',
  collapsed: 'Menu : replié (icônes). Cliquer pour passer en « étendu ».',
  extended: 'Menu : étendu. Cliquer pour repasser en « auto ».',
};

const THEME_ICON: Record<ThemePref, React.ReactNode> = {
  system: <Monitor size={18} weight="duotone" />,
  light:  <Sun     size={18} weight="duotone" />,
  dark:   <Moon    size={18} weight="duotone" />,
};
const THEME_LABEL: Record<ThemePref, string> = {
  system: 'Thème système',
  light:  'Thème clair',
  dark:   'Thème sombre',
};
const THEME_NEXT_HINT: Record<ThemePref, string> = {
  system: 'Passer en mode clair',
  light:  'Passer en mode sombre',
  dark:   'Passer en mode système',
};

/* AppShell monte Header une seule fois via HeaderShellRoot, qui enveloppe TOUT
   le reste de l'arbre (AppHeaderRegion + Outlet) dans le Provider. Les appels
   historiques <Header/> encore presents dans des pages (nettoyage en cours,
   cf docs) heritent ce contexte et deviennent des no-op. */
const HeaderMountedContext = createContext(false);

export function HeaderShellRoot({
  active,
  chromeless,
  children,
}: {
  active: boolean;
  /* La page a deja son propre burger via AppHeader (config.showBurger) :
     ne pas dupliquer la topbar/burger du rail par-dessus. */
  chromeless?: boolean;
  children: ReactNode;
}) {
  /* #root a `height: 100%` (index.css) — un bloc sans hauteur propre monte
     directement dessous hérite cette hauteur comme borne pour le calcul du
     conteneur de position:sticky, et se detache pres du bas des pages plus
     hautes que le viewport (verifie au navigateur : l'ecart correspond
     exactement a scrollY - (hauteur de #root - hauteur du bandeau)). Ce
     wrapper (bloc simple, sans flex ni hauteur imposee) devient le parent
     DOM reel du rail/AppHeader/Outlet a la place de #root, et grandit avec
     le contenu — la sticky reste correcte jusqu'au bas de la page. */
  return (
    <HeaderMountedContext.Provider value={true}>
      <div>
        {active && <HeaderInner chromeless={chromeless} />}
        {children}
      </div>
    </HeaderMountedContext.Provider>
  );
}

export default function Header(props: { floatingBurger?: boolean; chromeless?: boolean } = {}) {
  const alreadyMounted = useContext(HeaderMountedContext);
  if (alreadyMounted) return null;
  return (
    <HeaderMountedContext.Provider value={true}>
      <HeaderInner {...props} />
    </HeaderMountedContext.Provider>
  );
}

function HeaderInner({ floatingBurger = false, chromeless = false }: { floatingBurger?: boolean; chromeless?: boolean }) {
  const { session, loading } = useAuth();
  const isAdmin = useIsAdmin();
  const isTeacher = useIsTeacher();
  const { isDirector: isSchoolDirector } = useDirectionCapabilities();
  const { assignments, activeRole, activeAssignment, setActiveAssignment, children: linkedChildren, activeStudentId, setActiveStudent } = useRoles();

  const { pref: themePref, cycle: cycleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useSidemenuOpen();
  const [mode, setMode] = useState<RailMode>(() => {
    try {
      const m = localStorage.getItem(MODE_KEY);
      return m === 'collapsed' || m === 'extended' || m === 'auto' ? m : 'auto';
    } catch { return 'auto'; }
  });

  useEffect(() => {
    try { localStorage.setItem(MODE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);
  useEffect(() => {
    const el = document.documentElement;
    if (session) el.dataset.sidemenu = mode;
    else delete el.dataset.sidemenu;
    return () => { delete el.dataset.sidemenu; };
  }, [session, mode]);

  const cycleMode = () => setMode((m) => MODE_ORDER[(MODE_ORDER.indexOf(m) + 1) % MODE_ORDER.length]!);

  const onAdmin = location.pathname.startsWith('/admin/chat');
  const onTeacher = location.pathname.startsWith('/teacher');
  const showConvs = onTeacher || onAdmin;

  /* Active-role switcher (multi-role users only). */
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  /* Navigate directly to each role's real destination instead of /app: /app's
     AppRedirect resolves the destination from activeRole via its own effect,
     which can still see the pre-switch role for a render (TanStack's navigate
     doesn't commit synchronously) and send a fresh switch to the wrong space —
     same bug class already hardened in role-route.tsx. */
  const ROLE_HOME: Record<Role, string> = { admin: '/admin', direction: '/direction', teacher: '/teacher', tutor: '/tutor', eleve: '/mini-apps', ai_operator: '/admin/conversations' };
  const switchAssignment = (a: RoleAssignment) => {
    setRoleMenuOpen(false);
    if (a.role === activeAssignment?.role && a.schoolId === activeAssignment?.schoolId) return;
    setActiveAssignment(a);
    setOpen(false);
    navigate(ROLE_HOME[a.role]);
  };
  /* L'enfant actif est un choix de navigation : en changer ouvre la fiche de
     l'enfant choisi, sinon le rail pointerait vers un enfant que la page
     n'affiche pas. */
  const switchChild = (id: string) => {
    setActiveStudent(id);
    setOpen(false);
    navigate(`/tutor/enfant/${id}`);
  };
  /* Le rail suit la fiche réellement ouverte (liste « Mes enfants », boutons
     enfant, lien direct), pas seulement le sélecteur. */
  useEffect(() => {
    const id = /^\/tutor\/enfant\/([^/]+)/.exec(location.pathname)?.[1];
    if (id && id !== activeStudentId) setActiveStudent(id);
  }, [location.pathname, activeStudentId, setActiveStudent]);
  const [buildHash, setBuildHash] = useState<string | null>(null);
  useEffect(() => {
    fetch('/version.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: unknown) => { if (d && typeof d === 'object' && 'build' in d && typeof (d as { build: unknown }).build === 'string') setBuildHash((d as { build: string }).build); })
      .catch(() => { /* ignore */ });
  }, []);


  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  const signOut = async () => {
    setOpen(false);
    await authClient.signOut();
    navigate('/');
  };

  const brandHome = () => (
    <Link
      to={session ? '/app' : '/'}
      onClick={() => setOpen(false)}
      aria-label="Accueil Naïa-Edu"
      className="inline-flex items-center min-h-[44px] py-1.5 pl-2 pr-3 rounded-[var(--naia-r-sm)] bg-[var(--naia-card)] border border-[var(--naia-line)] shadow-[var(--naia-shadow-card)] no-underline"
    >
      <NaiaLogo height={18} />
    </Link>
  );

  const burgerBtn = (floating = false) => (
    <button
      type="button"
      className={floating
        ? 'fixed top-[max(6px,env(safe-area-inset-top))] left-[max(12px,env(safe-area-inset-left))] z-[60] w-11 h-11 bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-xl flex flex-col items-center justify-center gap-[5px] cursor-pointer shadow-[var(--naia-shadow-card)] p-0'
        : 'naia-burger w-11 h-11 bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-xl flex flex-col items-center justify-center gap-[5px] cursor-pointer shadow-[var(--naia-shadow-card)] p-0'}
      onClick={() => setOpen((v) => !v)}
      aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
      aria-expanded={open}
    >
      <Bar />
      <Bar />
      <Bar />
    </button>
  );

  return (
    <>
      {chromeless ? null : floatingBurger && !loading && session ? (
        open ? null : burgerBtn(true)
      ) : (
        <header className="relative flex items-center justify-between pt-[max(20px,env(safe-area-inset-top))] pb-5 pl-[max(24px,env(safe-area-inset-left))] pr-[max(24px,env(safe-area-inset-right))] w-full max-w-[1100px] mx-auto">
          <div className="flex items-center gap-3">
            {!loading && session && !open && burgerBtn()}
            {!loading && !session && brandHome()}
          </div>

          {!loading && !session && (
            <Link to="/sign-in" className="font-extrabold text-[var(--naia-card)] bg-[var(--naia-blue)] rounded-[var(--naia-r-sm)] px-[18px] py-3 min-h-[44px] inline-flex items-center shadow-[var(--naia-shadow-card)] border-none no-underline">
              Se connecter
            </Link>
          )}
        </header>
      )}

      {session && open && (
        <div aria-hidden className="naia-scrim fixed inset-0 bg-[rgba(7,16,28,0.40)] z-[49]" onClick={() => setOpen(false)} />
      )}

      {session && (
        <nav className={`naia-rail${open ? ' is-open' : ''}`} aria-label="Navigation principale">
          <div className="naia-rail__head shrink-0 flex items-center gap-2 mb-[14px] min-h-[40px]">
            <Link
              to="/app"
              onClick={() => setOpen(false)}
              aria-label="Accueil Naïa-Edu"
              className="naia-railbrand inline-flex items-center gap-[9px] flex-1 min-w-0 min-h-[44px] no-underline text-[var(--rail-text-active)] px-2 py-1.5 rounded-[9px] hover:bg-[var(--rail-surface-hover)] transition-colors"
            >
              <NaiaLogo height={18} onDark />
            </Link>
            <button
              type="button"
              className="naia-modecycle inline-flex items-center justify-center ml-auto w-11 h-11 p-0 bg-[var(--rail-surface)] border border-[var(--rail-line)] rounded-lg text-[var(--rail-text-muted)] cursor-pointer shrink-0 hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)] transition-colors"
              onClick={cycleMode}
              aria-label={MODE_HINT[mode]}
              title={MODE_HINT[mode]}
            >
              <span aria-hidden className="flex items-center justify-center">{MODE_ICON[mode]}</span>
            </button>
            <button
              type="button"
              className="naia-rail__close w-11 h-11 bg-[var(--rail-surface)] border border-[var(--rail-line)] rounded-lg flex items-center justify-center cursor-pointer text-[var(--rail-text-muted)] shrink-0 p-0 hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)] transition-colors"
              onClick={() => setOpen(false)}
              aria-label="Fermer le menu"
            >
              <X size={16} weight="bold" />
            </button>
          </div>

          <div className="naia-rail__scroll flex-1 min-h-0 flex flex-col overflow-y-auto">
            {/* Navigation propre au MODE ACTIF — un seul espace à la fois.
                Le changement de mode passe par le switcher en bas de sidebar,
                plus par des liens croisés ici. */}
            <RailNav
              isAdmin={isAdmin}
              isTeacher={isTeacher}
              isSchoolDirector={isSchoolDirector}
              activeRole={activeRole}
              current={location.pathname}
              onNav={() => setOpen(false)}
              linkedChildren={linkedChildren}
              activeStudentId={activeStudentId}
              onSelectChild={switchChild}
            />

            {/* Mes classes — section accordéon enseignant uniquement */}
            {isTeacher && (
              <RailClassList onNav={() => setOpen(false)} />
            )}

            {showConvs ? <RailConvList /> : <div className="flex-1" />}

            <ExamImportProgressRailItem />
            <BulkImportProgressRailItem />

            <Divider />
            {/* `/settings` est rendu ici, hors de RailNav, mais participe au même
                calcul : sans ça il resterait allumé par son propre préfixe. */}
            <SideItem
              to="/settings"
              icon="settings"
              current={location.pathname}
              active={
                activeRailPath(
                  railPaths({ isAdmin, isTeacher, isSchoolDirector, activeRole, linkedChildren, activeStudentId }),
                  location.pathname,
                ) === '/settings'
              }
              onNav={() => setOpen(false)}
            >
              Paramètres
            </SideItem>
            <button
              type="button"
              onClick={cycleTheme}
              aria-label={THEME_NEXT_HINT[themePref]}
              title={THEME_NEXT_HINT[themePref]}
              className="naia-side-item flex items-center gap-3 px-3 min-h-[44px] w-full rounded-[9px] bg-transparent border-none cursor-pointer font-[inherit] text-[var(--rail-text-muted)] hover:text-[var(--rail-text-active)] hover:bg-[var(--rail-surface-hover)] transition-colors text-[13px] font-semibold"
            >
              <span className="flex items-center justify-center w-5 h-5 shrink-0" aria-hidden>{THEME_ICON[themePref]}</span>
              <span className="naia-rail__label">{THEME_LABEL[themePref]}</span>
            </button>
            <SideItem
              to="/aide"
              icon={Question}
              current={location.pathname}
              active={
                activeRailPath(
                  railPaths({ isAdmin, isTeacher, isSchoolDirector, activeRole, linkedChildren, activeStudentId }),
                  location.pathname,
                ) === '/aide'
              }
              onNav={() => setOpen(false)}
            >
              Aide
            </SideItem>
            <Dialog>
              <DialogTrigger
                aria-label="Signaler un bug"
                title="Signaler un bug"
                className="naia-side-item flex items-center gap-3 px-3 min-h-[44px] w-full rounded-[9px] bg-transparent border-none cursor-pointer font-[inherit] text-[var(--rail-text-muted)] hover:text-[var(--rail-text-active)] hover:bg-[var(--rail-surface-hover)] transition-colors text-[13px] font-semibold"
              >
                <span className="flex items-center justify-center w-5 h-5 shrink-0" aria-hidden><Bug size={18} weight="duotone" /></span>
                <span className="naia-rail__label">Signaler un bug</span>
              </DialogTrigger>
              <DialogContent style={{ height: '80vh' }}>
                <DialogHeader>
                  <DialogTitle>Signaler un bug</DialogTitle>
                </DialogHeader>
                <iframe
                  src="https://protective-smile-58c.notion.site/ebd//29e9f5e7afa246bda10f6d3244ac6bec"
                  style={{ width: '100%', flexGrow: 1, border: 'none', borderRadius: '0 0 var(--naia-r-md) var(--naia-r-md)' }}
                  title="Formulaire de remontée de bug"
                  allow="clipboard-write"
                />
              </DialogContent>
            </Dialog>
          </div>
          <RailRoleSwitcher
            assignments={assignments}
            activeAssignment={activeAssignment}
            open={roleMenuOpen}
            setOpen={setRoleMenuOpen}
            onSwitch={switchAssignment}
          />
          <div className="naia-rail__footer shrink-0 flex items-center gap-2 px-2.5 py-2 rounded-[10px] bg-[var(--rail-surface)] border border-[var(--rail-line)] mt-1">
            <div className="w-7 h-7 rounded-full bg-[var(--rail-accent)] text-[var(--rail-accent-on)] flex items-center justify-center text-[10px] font-black shrink-0 select-none" aria-hidden>
              {(session.user?.email?.[0] ?? '?').toUpperCase()}
            </div>
            <div className="naia-rail__footer-info flex-1 min-w-0">
              <span className="block text-[11.5px] font-semibold text-[var(--rail-text-active)] break-all line-clamp-2" title={session.user?.email ?? undefined} data-testid="rail-user-email">{session.user?.email ?? ''}</span>
              {buildHash && <span className="block text-[10px] text-[var(--rail-text-active)]">v {buildHash}</span>}
            </div>
            <button type="button" className="w-11 h-11 p-0 rounded-[7px] shrink-0 bg-[var(--rail-surface)] border border-[var(--rail-line)] text-[var(--rail-text-muted)] grid place-items-center cursor-pointer hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)] transition-colors" onClick={signOut} aria-label="Déconnexion">
              <SignOut size={14} weight="bold" />
            </button>
          </div>
        </nav>
      )}
    </>
  );
}


